import { useThrottleFn } from 'ahooks';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { v4 as uuidv4 } from 'uuid';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { parseJsonSafely } from '../../ChatPage/utils/index';
import { FAST_QA_MODE } from '../../config/constant';
import { useMemo } from 'react';
import { Conversation } from '../../ChatPage/types/history.js';
// 取消请求
export const useCancelIwikiRequest = () => {
  const showErrorTips = useErrorTips();
  const { sessionId } = useSearchParamsAppoint();
  const { runAsync } = useRequestController({
    method: 'POST',
    url: `/api/agent/chat_cancel`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
  });

  const { run: handleStopStreaming } = useThrottleFn(
    async ({
      cancelRequest,
      user_id,
      messageId,
    }: {
      cancelRequest?: () => void;
      user_id: string;
      messageId: string;
    }) => {
      try {
        const result = await runAsync({
          user_id,
          session_id: sessionId,
          message_id: messageId,
          trace_id: uuidv4(),
        });
        cancelRequest?.();
        showErrorTips(result?.res_code);
      } catch (error) {
        console.log('handleStopStreaming', error);
      }
    },
    { wait: 2000 },
  );
  return {
    handleStopStreaming,
  };
};

// 获取anser数据
export const useGetAnswerData = (
  setConversations: (conversations: Conversation[]) => void,
) => {
  const { sessionId, msgId } = useSearchParamsAppoint();
  const showErrorTips = useErrorTips();
  const data = useRequestController({
    method: 'POST',
    url: `/api/agent_com/get_answer`,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    refreshDeps: useMemo(
      () => ({
        session_id: sessionId,
        message_id: msgId,
        trace_id: uuidv4(),
      }),
      [msgId, sessionId],
    ),
    ready: !!msgId,
    onSuccess: (data) => {
      const formattedAnswer: any[] = [];
      const list = parseJsonSafely(data?.answer);
      showErrorTips(data?.res_code);
      // TODO: 全局重构
      list?.forEach((item: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, data, text, metadata } = item;

        if (type === 'text' && text) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          formattedAnswer.push({
            type: 'text',
            value: text,
          });
          return;
        }

        if (type === 'data' && data) {
          const {
            type: dataType,
            value,
            new_agent,
            previous_agent,
            input,
          } = data;

          // 第一步：过滤 Planner Agent、Summary Agent 切换
          if (
            dataType === 'switch_agent' &&
            (new_agent === 'Planner Agent' || new_agent === 'Summary Agent')
          ) {
            return;
          }
          // 处理 think 类型
          if (dataType === 'think') {
            // 查找最后一个 think 项
            const lastItem = formattedAnswer[formattedAnswer.length - 1];
            if (lastItem?.type === 'think' && Array.isArray(lastItem.value)) {
              // 合并到现有 think 项
              const thinkItems = lastItem.value;
              const lastThinkItem = thinkItems[thinkItems.length - 1];
              if (lastThinkItem?.type === 'think') {
                lastThinkItem.value += value;
              } else {
                thinkItems.push({
                  type: 'think',
                  value,
                });
              }
            } else {
              // 创建新 think 项
              formattedAnswer.push({
                type: 'think',
                status: 'success',
                value: [
                  {
                    type: 'think',
                    value,
                  },
                ],
              } as unknown);
            }
            return;
          }

          // 处理 plan_json 类型
          if (dataType === 'plan_json') {
            let planValue = value;
            try {
              const parsed = JSON.parse(value);
              if (parsed?.rephrase) {
                planValue = parsed.rephrase;
              }
            } catch (e) {
              // 解析失败时保持原值
            }

            // 查找最后一个 think 项
            const lastItem = formattedAnswer[formattedAnswer.length - 1];
            if (lastItem?.type === 'think' && Array.isArray(lastItem.value)) {
              // 合并到现有 think 项
              const thinkItems = lastItem.value;
              const lastThinkItem = thinkItems[thinkItems.length - 1];
              if (lastThinkItem?.type === 'plan_json') {
                lastThinkItem.value += planValue;
              } else {
                thinkItems.push({
                  type: 'plan_json',
                  value: planValue,
                });
              }
            } else {
              // 创建新 think 项
              formattedAnswer.push({
                type: 'think',
                status: 'success',
                value: [
                  {
                    type: 'plan_json',
                    value: planValue,
                  },
                ],
              } as unknown);
            }
            return;
          }
          // 处理 switch_agent 的特殊情况
          if (dataType === 'switch_agent') {
            // const isRepeat = new_agent === prevAgent;
            // prevAgent = new_agent;

            // 计算当前input的数量
            let inputIndex = 1;
            formattedAnswer.forEach((item) => {
              if (item.type === 'think' && Array.isArray(item.value)) {
                item.value.forEach((thinkItem) => {
                  if (thinkItem.type === 'switch_agent') {
                    // eslint-disable-next-line no-plusplus
                    inputIndex++;
                  }
                });
              }
            });

            const thinkItem = {
              type: dataType,
              new_agent,
              previous_agent,
              value: input || '',
              input,
              inputIndex, // 有input时显示序号，无input时为0
            };

            // 查找最后一个 think 项
            const lastItem = formattedAnswer[formattedAnswer.length - 1];
            if (lastItem?.type === 'think') {
              lastItem.value.push(thinkItem);
            } else {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              formattedAnswer.push({
                type: 'think',
                status: 'success',
                value: [thinkItem],
              });
            }
            return;
          }

          // 保留原有 tool_use 处理逻辑
          if (dataType === 'tool_use') {
            const lastItem = formattedAnswer[formattedAnswer.length - 1];
            const thinkItem = {
              type: dataType,
              value,
              new_agent,
              previous_agent,
            };

            if (lastItem?.type === 'think') {
              lastItem.value.push(thinkItem);
            } else {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              formattedAnswer.push({
                type: 'think',
                status: 'success',
                value: [thinkItem],
              });
            }
            return;
          }

          if (dataType === 'reference') {
            const referenceValue = parseJsonSafely(value);
            const referenceArray = Array.isArray(referenceValue)
              ? referenceValue
              : [referenceValue];

            // 查找最后一个项是否也是 reference 类型
            const lastIndex = formattedAnswer.length - 1;
            const lastItem = formattedAnswer[lastIndex];

            if (lastItem && lastItem.type === 'reference') {
              // 如果最后一项是 reference 类型，则合并到其 value 数组中
              lastItem.value.push(...referenceArray);
            } else {
              // 创建新的 reference 项
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              formattedAnswer.push({
                type: 'reference',
                value: referenceArray,
              });
            }
            return;
          }

          if (dataType === 'bi_data') {
            const biDataValue = parseJsonSafely(value);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            formattedAnswer.push({
              type: 'biData',
              value: biDataValue,
            });
            return;
          }

          if (dataType === 'sensitive_data_info') {
            const sensitiveValue = parseJsonSafely(value);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            formattedAnswer.push({
              type: 'sensitiveInfo',
              value: sensitiveValue,
            });
            return;
          }

          if (dataType === 'warning') {
            const existingWarning = formattedAnswer.find(
              item => item.type === 'costWarning',
            );
            if (existingWarning) {
              return;
            }
            const warningValue = parseJsonSafely(value);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const warningItem = {
              type: 'costWarning',
              value: warningValue,
            };
            const insertIdx =
              formattedAnswer.findIndex(item => item.type === 'think') + 1;
            formattedAnswer.splice(insertIdx, 0, warningItem);
            return;
          }
        }
      });
      const dataSource = [
        {
          answer: formattedAnswer,
          mode: FAST_QA_MODE,
          messageId: data?.message_id,
          thinkTimeSeconds: data?.think_time_seconds,
          like: data?.user_like,
        },
      ];
      setConversations(dataSource);
    },
  });

  return {
    ...data,
  };
};
